import { useEffect, useState } from "react";
import { File, Image as ImageIcon, Lock, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage, cn } from "@link/ui";
import { isIgnored, onIgnoredAccountsChanged } from "../../lib/ignored";
import { previewReply, type ReplyPreview as ReplyDetails } from "../../lib/message-replies";
import type { MessageView } from "../../types/ui";
import { RoleName, type NameRole } from "../RoleName";

export function ReplyPreview({ reply, nameRole }: { reply: ReplyDetails; nameRole?: NameRole | undefined }) {
  const [ignored, setIgnored] = useState(() => reply.authorXid ? isIgnored(reply.authorXid) : false);
  const [notVisible, setNotVisible] = useState(false);
  useEffect(() => {
    const update = () => setIgnored(reply.authorXid ? isIgnored(reply.authorXid) : false);
    update(); setNotVisible(false);
    return onIgnoredAccountsChanged(update);
  }, [reply.authorXid, reply.xid]);
  const media = reply.kind === "image" || reply.kind === "gif" || reply.kind === "file";
  const Icon = reply.kind === "image" || reply.kind === "gif" ? ImageIcon : reply.kind === "secret" ? Lock : File;
  const preview = ignored ? "Message from ignored user" : notVisible ? "Original message isn’t visible in this view" : reply.preview;
  return <button type="button" disabled={reply.unavailable}
    className="relative mb-1 flex h-5 w-full min-w-0 items-center gap-1.5 text-left text-xs text-cloud-500 hover:text-cloud-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-strawberry-400 disabled:cursor-default"
    aria-label={reply.unavailable ? preview : `View original message from ${reply.authorName}: ${preview}`}
    onClick={event => {
      // Scope to this chat, not another open pane or an interpolated CSS selector.
      const list = event.currentTarget.closest("[data-message-list]");
      const target = [...(list?.querySelectorAll<HTMLElement>("[data-message-id]") ?? [])].find(row => row.dataset.messageId === reply.xid);
      if (!target) { setNotVisible(true); return; }
      target.scrollIntoView({ block: "center", behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
      target.focus({ preventScroll: true });
    }}>
    <span aria-hidden className="pointer-events-none absolute -left-[31px] top-[10px] h-3 w-6 rounded-tl-md border-l-2 border-t-2 border-cloud-300" />
    {!reply.unavailable && <>
      <Avatar className="h-4 w-4 shrink-0">{reply.authorAvatarUrl && <AvatarImage src={reply.authorAvatarUrl} alt="" />}<AvatarFallback>{reply.authorName.slice(0, 2)}</AvatarFallback></Avatar>
      <RoleName name={reply.authorName} role={nameRole} className="max-w-[35%] shrink-0 font-semibold text-cloud-600" />
    </>}
    <span className={cn("min-w-0 truncate", media && !ignored && "italic")}>{preview}</span>
    {!reply.unavailable && !ignored && (media || reply.kind === "secret") && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />}
  </button>;
}

export function ComposerReplyPreview({ message, onCancel }: { message: MessageView; onCancel: () => void }) {
  const reply = previewReply(message);
  return <div className="mb-2 flex items-center gap-2 rounded-xl bg-cloud-50 px-3 py-2 text-xs text-cloud-600">
    <span className="min-w-0 flex-1 truncate">Replying to <strong>{reply.authorName}</strong>: {reply.kind === "image" ? "Image attachment" : reply.kind === "file" ? "Attachment" : reply.kind === "gif" ? "GIF" : reply.preview}</span>
    <button type="button" onClick={onCancel} className="rounded-md p-1 hover:bg-cloud-200" aria-label="Cancel reply"><X className="h-3.5 w-3.5" /></button>
  </div>;
}
